/* Audit Logs — read-only history of admin / creator changes.
   Route: #/mainlayout/audit-logs. Data: GET /audit-logs (backend card Vgb6pqVH).

   - Applied filters live in the URL (refresh / Back keep them). The filter bar
     edits a draft; Apply writes it to the URL and resets to page 1.
   - No edit / delete / clear controls anywhere: logs are immutable.
   - Gated by can('audit_logs','view'); the backend enforces it too (403). */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { IoAlertCircleOutline, IoClose, IoDocumentTextOutline, IoDownloadOutline, IoEyeOutline, IoLockClosedOutline } from "react-icons/io5";
import s from "../userManagement/userManagement.module.css";
import a from "./AuditLogs.module.css";
import { useRbac } from "../../rbac/RbacContext";
import { listUsers } from "../../rbac/rbacService";
import useUpdateSearchParams from "../../hooks/useUpdateSearchParams";
import { showToast } from "../../utils/Toastify/Toastify";
import { EmptyState, Pagination, SearchInput } from "../userManagement/uiBits";
import { exportAuditLogs, getAuditLogMeta, listAuditLogs } from "./auditLogsService";
import {
  AUDIT_ACTIONS,
  AUDIT_MODULES,
  PAGE_SIZES,
  PRESETS,
  STATUSES,
  actionTone,
  filtersFromSearchParams,
  filtersToSearchParams,
  formatTimestamp,
  fromLocalInputValue,
  humanize,
  presetRange,
  showingText,
  summarize,
  toLocalInputValue,
  validateRange,
  toAuditUserId,
  fieldLabel,
  flattenChanges,
  formatScalar,
  itemLabel,
} from "./auditLogsUtils";

/* ------------------------------------------------------------------------ */
/* Multi-select dropdown (checkbox list, optional search)                    */
/* ------------------------------------------------------------------------ */
const MultiSelect = ({ label, options, selected, onChange, searchable, onSearch, placeholder, labelOf }) => {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => ref.current && !ref.current.contains(e.target) && setOpen(false);
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDoc);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const visible = onSearch
    ? options
    : options.filter((o) => !term || `${o.label} ${o.sub || ""}`.toLowerCase().includes(term.toLowerCase()));

  const toggle = (value) =>
    onChange(selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value]);

  const single = selected.length === 1 ? options.find((x) => x.value === selected[0]) : null;
  const text = selected.length === 0 ? placeholder : selected.length === 1
    ? labelOf?.(selected[0]) || single?.label || "1 selected"
    : `${selected.length} selected`;

  return (
    <div className={a.filterGroup}>
      <span className={a.filterLabel}>{label}</span>
      <div className={a.multi} ref={ref}>
        <button
          type="button"
          className={a.multiTrigger}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label={`${label} filter`}
          onClick={() => setOpen((o) => !o)}
        >
          {text}
        </button>
        {open && (
          <div className={a.multiPanel}>
            {searchable && (
              <input
                className={a.multiSearch}
                autoFocus
                value={term}
                placeholder={`Search ${label.toLowerCase()}…`}
                onChange={(e) => {
                  setTerm(e.target.value);
                  onSearch && onSearch(e.target.value);
                }}
              />
            )}
            <div className={a.multiList} role="listbox" aria-multiselectable="true">
              {visible.length === 0 && <div className={a.multiEmpty}>No matches</div>}
              {visible.map((o) => (
                <label key={o.value} className={a.multiOption}>
                  <input
                    type="checkbox"
                    checked={selected.includes(o.value)}
                    onChange={() => toggle(o.value)}
                  />
                  <span>
                    {o.label}
                    {o.sub && <span className={a.multiOptionSub}>{o.sub}</span>}
                  </span>
                </label>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------------ */
/* Detail drawer                                                             */
/* ------------------------------------------------------------------------ */
// Stored values as plain text: nested settings become an indented
// "Label: value" list rather than raw JSON.
const Readable = ({ value }) => {
  if (Array.isArray(value)) {
    if (value.length === 0) return "None";
    if (value.every((x) => x === null || typeof x !== "object")) return value.map(formatScalar).join(", ");
    return (
      <div className={a.nested}>
        {value.map((item, i) => (
          <div key={i} className={a.nestedItem}>
            <div className={a.nestedTitle}>{itemLabel(item, i)}</div>
            <Readable value={item} />
          </div>
        ))}
      </div>
    );
  }
  if (value !== null && typeof value === "object") {
    const entries = Object.entries(value);
    if (entries.length === 0) return "—";
    return (
      <div className={a.nested}>
        {entries.map(([k, v]) => (
          <div key={k} className={a.nestedRow}>
            <span className={a.nestedKey}>{fieldLabel(k)}: </span>
            <Readable value={v} />
          </div>
        ))}
      </div>
    );
  }
  return formatScalar(value);
};

// A whole entry added to / removed from a list: one line, settings on demand.
const ChangeCell = ({ value, other, verb, entry }) => {
  const isEntry = entry && value !== null && typeof value === "object" && !Array.isArray(value);
  if (!isEntry || (other !== null && other !== undefined)) return <Readable value={value} />;
  return (
    <details className={a.entry}>
      <summary>
        {verb} “{itemLabel(value)}”
      </summary>
      <div className={a.entryBody}>
        <Readable value={value} />
      </div>
    </details>
  );
};

const KeyValueTable = ({ obj }) => (
  <table className={a.diffTable}>
    <tbody>
      {Object.entries(obj || {}).map(([k, v]) => (
        <tr key={k}>
          <th style={{ width: "35%" }}>{fieldLabel(k)}</th>
          <td>
            <Readable value={v} />
          </td>
        </tr>
      ))}
    </tbody>
  </table>
);

const DetailDrawer = ({ item, onClose }) => {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const changes = item.changes || {};
  const diff = changes.diff && typeof changes.diff === "object" ? changes.diff : null;
  const diffRows = useMemo(
    () =>
      diff ? Object.entries(diff).flatMap(([field, change]) => flattenChanges(field, change?.before, change?.after)) : [],
    [diff]
  );

  return (
    <div className={s.modalOverlay} onClick={onClose}>
      <div className={s.drawer} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Audit log entry">
        <div className={s.drawerHeader}>
          <div>
            <h3 className={s.drawerTitle}>{summarize(item)}</h3>
            <p className={s.drawerSub}>{formatTimestamp(item.timestamp)}</p>
          </div>
          <button className={s.iconBtn} onClick={onClose} aria-label="Close">
            <IoClose />
          </button>
        </div>
        <div className={s.drawerBody}>
          <div className={a.detailList}>
            <span className={a.detailKey}>User</span>
            <span className={a.detailVal}>
              {item.userName || "—"}
              {item.userEmail ? ` (${item.userEmail})` : ""}
            </span>
            <span className={a.detailKey}>Role</span>
            <span className={a.detailVal}>{item.role || "—"}</span>
            <span className={a.detailKey}>Action</span>
            <span className={a.detailVal}>
              <span className={`${a.badge} ${a[`tone_${actionTone(item.action)}`]}`}>{humanize(item.action)}</span>
            </span>
            <span className={a.detailKey}>Module</span>
            <span className={a.detailVal}>{humanize(item.module) || "—"}</span>
            <span className={a.detailKey}>Item</span>
            <span className={a.detailVal}>
              {[item.entityType, item.entityName].filter(Boolean).join(": ") || "—"}
              {item.entityId ? ` (#${item.entityId})` : ""}
            </span>
            <span className={a.detailKey}>Status</span>
            <span className={a.detailVal}>{humanize(item.status) || "—"}</span>
            <span className={a.detailKey}>IP</span>
            <span className={a.detailVal}>{item.ip || "—"}</span>
            <span className={a.detailKey}>User agent</span>
            <span className={a.detailVal}>{item.userAgent || "—"}</span>
          </div>

          {diffRows.length > 0 && (
            <>
              <div className={a.sectionHeading}>Changes</div>
              <table className={a.diffTable}>
                <thead>
                  <tr>
                    <th style={{ width: "28%" }}>Field</th>
                    <th>Before</th>
                    <th>After</th>
                  </tr>
                </thead>
                <tbody>
                  {diffRows.map((row, i) => (
                    <tr key={`${i}:${row.label}`}>
                      <td>{row.label}</td>
                      <td className={a.before}>
                        <ChangeCell value={row.before} other={row.after} verb="Removed" entry={row.entry} />
                      </td>
                      <td className={a.after}>
                        <ChangeCell value={row.after} other={row.before} verb="Added" entry={row.entry} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
          {changes.snapshot && (
            <>
              <div className={a.sectionHeading}>Snapshot before delete</div>
              <KeyValueTable obj={changes.snapshot} />
            </>
          )}
          {changes.after && (
            <>
              <div className={a.sectionHeading}>Created values</div>
              <KeyValueTable obj={changes.after} />
            </>
          )}
          {changes.meta && (
            <>
              <div className={a.sectionHeading}>Details</div>
              <KeyValueTable obj={changes.meta} />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------------ */
/* Page                                                                      */
/* ------------------------------------------------------------------------ */
const COLS = 9;

const AuditLogs = () => {
  const { can, roles } = useRbac();
  const allowed = can("audit_logs", "view");
  const [searchParams, updateParams] = useUpdateSearchParams();
  const urlKey = searchParams.toString();

  // Applied filters = the URL. Recomputed only when the URL changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const applied = useMemo(() => filtersFromSearchParams(searchParams, new Date()), [urlKey]);

  const [draft, setDraft] = useState(applied);
  useEffect(() => setDraft(applied), [applied]);

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [forbidden, setForbidden] = useState(false);
  const [rangeError, setRangeError] = useState("");
  const [selected, setSelected] = useState(null);
  const [exportAvailable, setExportAvailable] = useState(true);
  const [exporting, setExporting] = useState(false);

  const [meta, setMeta] = useState({ actions: AUDIT_ACTIONS, modules: AUDIT_MODULES });
  const [userOptions, setUserOptions] = useState([]);
  const [userLabels, setUserLabels] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const res = await listAuditLogs(applied);
      setItems(res.items);
      setTotal(res.total);
      setRangeError("");
    } catch (err) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.message;
      setItems([]);
      setTotal(0);
      if (status === 403) {
        setForbidden(true);
      } else if (status === 422 && (err?.response?.data?.errors?.from || err?.response?.data?.errors?.to)) {
        const errors = err.response.data.errors;
        setRangeError(errors.from || errors.to);
      } else {
        setLoadError(msg || "Failed to load audit logs");
        showToast(msg || "Failed to load audit logs", "error");
      }
    } finally {
      setLoading(false);
    }
  }, [applied]);

  useEffect(() => {
    if (allowed) load();
  }, [allowed, load]);

  useEffect(() => {
    if (!allowed) return;
    getAuditLogMeta().then((m) => m && setMeta({ actions: m.actions, modules: m.modules }));
  }, [allowed]);

  // Users for the searchable filter come from the back-office users list.
  // The list shows name + email; once picked, the trigger and chip show the
  // email, since names repeat (two "rose"s) and can just be the tenant name.
  const searchUsers = useCallback(async (term = "") => {
    try {
      const res = await listUsers({ search: term, page: 1, pageSize: 20 });
      const list = (res && (res.data || res.items)) || [];
      const users = list.map((u) => ({ ...u, id: toAuditUserId(u.id) }));
      setUserOptions(users.map((u) => ({ value: u.id, label: u.name || u.email, sub: u.email })));
      setUserLabels((prev) => {
        const next = { ...prev };
        users.forEach((u) => {
          next[u.id] = u.email || u.name;
        });
        return next;
      });
    } catch {
      setUserOptions([]);
    }
  }, []);
  const searchTimer = useRef(null);
  const onUserSearch = (term) => {
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => searchUsers(term.trim()), 300);
  };
  useEffect(() => {
    if (allowed) searchUsers("");
    return () => clearTimeout(searchTimer.current);
  }, [allowed, searchUsers]);

  const now = new Date();
  const maxInput = toLocalInputValue(now);

  const writeUrl = (filters) => updateParams(filtersToSearchParams(filters));

  const apply = () => {
    const err = validateRange(draft.from, draft.to, new Date());
    if (err) {
      setRangeError(err);
      return;
    }
    setRangeError("");
    writeUrl({ ...draft, page: 1 });
  };

  const reset = () => {
    setRangeError("");
    const fresh = filtersFromSearchParams(new URLSearchParams(), new Date());
    setDraft(fresh);
    writeUrl({ ...fresh, limit: applied.limit });
  };

  // Editing the range clears the old error; Apply re-validates.
  const setDraftRange = (patch) => {
    setRangeError("");
    setDraft((d) => ({ ...d, preset: "custom", ...patch }));
  };

  const setPreset = (preset) => {
    setRangeError("");
    if (preset === "custom") {
      setDraft((d) => ({ ...d, preset }));
      return;
    }
    const { from, to } = presetRange(preset, new Date());
    setDraft((d) => ({ ...d, preset, from, to }));
  };

  const removeChip = (patch) => writeUrl({ ...applied, ...patch, page: 1 });

  const doExport = async () => {
    setExporting(true);
    try {
      const blob = await exportAuditLogs(applied);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `audit-logs-${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      if (err?.response?.status === 404) {
        setExportAvailable(false);
        showToast("CSV export isn't available on this server", "info");
      } else {
        showToast(err?.response?.data?.message || "Export failed", "error");
      }
    } finally {
      setExporting(false);
    }
  };

  if (!allowed || forbidden) {
    return (
      <div className={s.page}>
        <EmptyState
          icon={<IoLockClosedOutline />}
          title="You don't have access to Audit Logs"
          message="Ask an admin to grant the Audit Logs view permission to your role."
        />
      </div>
    );
  }

  const roleOptions = (roles || []).map((r) => ({ value: r.name, label: r.name }));
  const chips = [];
  if (applied.preset !== "today") {
    chips.push({
      key: "range",
      label:
        applied.preset === "custom"
          ? `${formatTimestamp(applied.from, { seconds: false })} → ${formatTimestamp(applied.to, { seconds: false })}`
          : PRESETS.find((p) => p.key === applied.preset)?.label,
      patch: { preset: "today", from: null, to: null },
    });
  }
  applied.userIds.forEach((id) =>
    chips.push({ key: `u${id}`, label: `User: ${userLabels[id] || id}`, patch: { userIds: applied.userIds.filter((x) => x !== id) } })
  );
  applied.roles.forEach((r) =>
    chips.push({ key: `r${r}`, label: `Role: ${r}`, patch: { roles: applied.roles.filter((x) => x !== r) } })
  );
  applied.actions.forEach((x) =>
    chips.push({ key: `a${x}`, label: `Action: ${humanize(x)}`, patch: { actions: applied.actions.filter((y) => y !== x) } })
  );
  applied.modules.forEach((x) =>
    chips.push({ key: `m${x}`, label: `Module: ${humanize(x)}`, patch: { modules: applied.modules.filter((y) => y !== x) } })
  );
  if (applied.status) chips.push({ key: "status", label: `Status: ${humanize(applied.status)}`, patch: { status: "" } });
  if (applied.q) chips.push({ key: "q", label: `Search: “${applied.q}”`, patch: { q: "" } });

  return (
    <div className={s.page}>
      <div className={s.header}>
        <div className={s.headerTitleWrap}>
          <h1 className={s.title}>Audit Logs</h1>
          <p className={s.subtitle}>Every admin and creator change, newest first. Logs are read-only.</p>
        </div>
        <div className={s.headerActions}>
          {exportAvailable && (
            <button className={s.btnGhost} onClick={doExport} disabled={exporting}>
              <IoDownloadOutline /> {exporting ? "Exporting…" : "Export CSV"}
            </button>
          )}
        </div>
      </div>

      <form
        className={a.filterBar}
        onSubmit={(e) => {
          e.preventDefault();
          apply();
        }}
      >
        <div className={a.filterGroup}>
          <span className={a.filterLabel}>Date &amp; time</span>
          <div className={a.dateRow}>
            <select
              className={`${s.filterSelect} ${a.select}`}
              aria-label="Date range preset"
              value={draft.preset}
              onChange={(e) => setPreset(e.target.value)}
            >
              {PRESETS.map((p) => (
                <option key={p.key} value={p.key}>
                  {p.label}
                </option>
              ))}
            </select>
            <input
              type="datetime-local"
              className={`${a.dateInput} ${rangeError ? a.dateInvalid : ""}`}
              aria-label="From"
              aria-invalid={!!rangeError}
              max={maxInput}
              value={toLocalInputValue(draft.from)}
              onChange={(e) => setDraftRange({ from: fromLocalInputValue(e.target.value) })}
            />
            <input
              type="datetime-local"
              className={`${a.dateInput} ${rangeError ? a.dateInvalid : ""}`}
              aria-label="To"
              aria-invalid={!!rangeError}
              max={maxInput}
              value={toLocalInputValue(draft.to)}
              onChange={(e) => setDraftRange({ to: fromLocalInputValue(e.target.value) })}
            />
          </div>
          {rangeError && (
            <span className={a.rangeError} role="alert">
              <IoAlertCircleOutline aria-hidden="true" /> {rangeError}
            </span>
          )}
        </div>

        <MultiSelect
          label="User"
          placeholder="All users"
          searchable
          onSearch={onUserSearch}
          options={[
            ...userOptions,
            ...draft.userIds
              .filter((id) => !userOptions.some((o) => o.value === id))
              .map((id) => ({ value: id, label: userLabels[id] || id })),
          ]}
          selected={draft.userIds}
          labelOf={(id) => userLabels[id]}
          onChange={(userIds) => setDraft((d) => ({ ...d, userIds }))}
        />
        <MultiSelect
          label="Role"
          placeholder="All roles"
          options={roleOptions}
          selected={draft.roles}
          onChange={(rolesSel) => setDraft((d) => ({ ...d, roles: rolesSel }))}
        />
        <MultiSelect
          label="Action"
          placeholder="All actions"
          searchable
          options={meta.actions.map((x) => ({ value: x, label: humanize(x) }))}
          selected={draft.actions}
          onChange={(actions) => setDraft((d) => ({ ...d, actions }))}
        />
        <MultiSelect
          label="Module"
          placeholder="All modules"
          searchable
          options={meta.modules.map((x) => ({ value: x, label: humanize(x) }))}
          selected={draft.modules}
          onChange={(modules) => setDraft((d) => ({ ...d, modules }))}
        />
        <div className={a.filterGroup}>
          <span className={a.filterLabel}>Status</span>
          <select
            className={`${s.filterSelect} ${a.select}`}
            aria-label="Status"
            value={draft.status}
            onChange={(e) => setDraft((d) => ({ ...d, status: e.target.value }))}
          >
            <option value="">All statuses</option>
            {STATUSES.map((x) => (
              <option key={x} value={x}>
                {humanize(x)}
              </option>
            ))}
          </select>
        </div>
        <div className={`${a.filterGroup} ${a.searchGroup}`}>
          <span className={a.filterLabel}>Search</span>
          <SearchInput
            value={draft.q}
            onChange={(e) => setDraft((d) => ({ ...d, q: e.target.value }))}
            placeholder="User, item, ID or summary…"
          />
        </div>
        <div className={a.filterActions}>
          <button type="button" className={s.btnGhost} onClick={reset}>
            Reset
          </button>
          <button type="submit" className={s.btnPrimary}>
            Apply
          </button>
        </div>
      </form>

      {chips.length > 0 && (
        <div className={a.chips} aria-label="Active filters">
          {chips.map((c) => (
            <span key={c.key} className={a.chip}>
              {c.label}
              <button type="button" className={a.chipRemove} aria-label={`Remove ${c.label}`} onClick={() => removeChip(c.patch)}>
                <IoClose />
              </button>
            </span>
          ))}
        </div>
      )}

      <div className={s.tableCard}>
        {loadError && !loading ? (
          <div className={a.errorPanel}>
            <span>{loadError}</span>
            <button className={s.btnPrimary} onClick={load}>
              Retry
            </button>
          </div>
        ) : !loading && items.length === 0 ? (
          <EmptyState icon={<IoDocumentTextOutline />} title="No activity found for the selected filters" />
        ) : (
          <>
            <div className={s.tableScroll}>
              <table className={s.table} style={{ minWidth: 1100 }}>
                <thead>
                  <tr>
                    <th>Date &amp; Time</th>
                    <th>User</th>
                    <th>Role</th>
                    <th>Action</th>
                    <th>Module</th>
                    <th>Item</th>
                    <th>Summary</th>
                    <th>IP</th>
                    <th className={`${s.colActions} ${a.stickyCol}`}>View</th>
                  </tr>
                </thead>
                <tbody>
                  {loading
                    ? Array.from({ length: 6 }, (_, i) => (
                        <tr key={i} className={a.skeletonRow} data-testid="audit-skeleton">
                          {Array.from({ length: COLS }, (__, j) => (
                            <td key={j}>
                              <div className={a.skeletonBar} />
                            </td>
                          ))}
                        </tr>
                      ))
                    : items.map((item) => (
                        <tr key={item.id}>
                          <td className={a.nowrap}>{formatTimestamp(item.timestamp)}</td>
                          <td>
                            <div className={s.userCellMeta}>
                              <span className={s.userName}>{item.userName || "—"}</span>
                              {item.userEmail && <span className={s.userEmail}>{item.userEmail}</span>}
                            </div>
                          </td>
                          <td>{item.role || "—"}</td>
                          <td>
                            <span className={`${a.badge} ${a[`tone_${actionTone(item.action)}`]}`}>
                              {humanize(item.action)}
                            </span>
                            {item.status === "failed" && <span className={a.muted}> · Failed</span>}
                          </td>
                          <td className={a.nowrap}>{humanize(item.module)}</td>
                          <td>{item.entityName || (item.entityId ? `#${item.entityId}` : "—")}</td>
                          <td>
                            <div className={a.summary} title={summarize(item)}>
                              {summarize(item)}
                            </div>
                          </td>
                          <td className={a.muted}>{item.ip || "—"}</td>
                          <td className={`${s.colActions} ${a.stickyCol}`}>
                            <button className={`${s.btnGhost} ${a.viewBtn}`} onClick={() => setSelected(item)} aria-label="View entry">
                              <IoEyeOutline /> View
                            </button>
                          </td>
                        </tr>
                      ))}
                </tbody>
              </table>
            </div>
            <div className={a.showing}>
              <span>{loading ? "Loading…" : showingText(applied.page, applied.limit, total)}</span>
              <label className={a.pageSize}>
                Rows per page
                <select
                  className={s.filterSelect}
                  aria-label="Rows per page"
                  value={applied.limit}
                  onChange={(e) => writeUrl({ ...applied, limit: Number(e.target.value), page: 1 })}
                >
                  {PAGE_SIZES.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            {!loading && (
              <Pagination
                page={applied.page}
                pageSize={applied.limit}
                total={total}
                onChange={(p) => writeUrl({ ...applied, page: p })}
              />
            )}
          </>
        )}
      </div>

      {selected && <DetailDrawer item={selected} onClose={() => setSelected(null)} />}
    </div>
  );
};

export default AuditLogs;
